/**
 * First-party acquisition attribution.
 *
 * Every page load records a "touch" (referrer, UTM tags, ad click IDs, landing
 * path) into localStorage only. Nothing leaves the browser until the visitor
 * creates an account — at which point the first touch and the most recent touch
 * are written once to `user_attribution`, so we can answer "where did this
 * customer actually come from?" instead of guessing.
 *
 * No IP address, no third-party transmission, no cross-site identifier.
 */
import { supabase } from "@/integrations/supabase/client";
import { organicSource } from "../../supabase/functions/_shared/organic-source";

const STORAGE_KEY = "os_attribution_v1";

export type Touch = {
  channel: string;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  referrer: string | null;
  referrer_host: string | null;
  landing_path: string | null;
  click_id: string | null;
  click_id_type: string | null;
  at: string;
};

type Stored = { first: Touch; last: Touch; count: number };

const CLICK_IDS: Record<string, string> = {
  gclid: "google_ads",
  gbraid: "google_ads",
  wbraid: "google_ads",
  fbclid: "meta_ads",
  ttclid: "tiktok_ads",
  msclkid: "microsoft_ads",
  twclid: "x_ads",
  li_fat_id: "linkedin_ads",
};

/** Referrer host → channel. Checked as a substring of the hostname. */
const REFERRER_CHANNELS: Array<[string, string]> = [
  ["chatgpt.com", "ai_assistant"],
  ["chat.openai.com", "ai_assistant"],
  ["perplexity.ai", "ai_assistant"],
  ["claude.ai", "ai_assistant"],
  ["gemini.google.com", "ai_assistant"],
  ["copilot.microsoft.com", "ai_assistant"],
  ["grok.com", "ai_assistant"],
  ["x.ai", "ai_assistant"],
  ["google.", "organic_search"],
  ["bing.com", "organic_search"],
  ["duckduckgo.com", "organic_search"],
  ["yahoo.com", "organic_search"],
  ["ecosia.org", "organic_search"],
  ["brave.com", "organic_search"],
  ["reddit.com", "reddit"],
  ["producthunt.com", "product_hunt"],
  ["g2.com", "review_site"],
  ["capterra.com", "review_site"],
  ["alternativeto.net", "review_site"],
  ["trustpilot.com", "review_site"],
  ["youtube.com", "youtube"],
  ["youtu.be", "youtube"],
  ["linkedin.com", "linkedin"],
  ["twitter.com", "x_twitter"],
  ["t.co", "x_twitter"],
  ["x.com", "x_twitter"],
  ["facebook.com", "facebook"],
  ["instagram.com", "instagram"],
  ["tiktok.com", "tiktok"],
  ["t.me", "telegram"],
  ["discord.com", "discord"],
  ["news.ycombinator.com", "hacker_news"],
  ["quora.com", "quora"],
  ["medium.com", "referral_blog"],
  ["mail.google.com", "email"],
  ["outlook.", "email"],
];

function classify(params: URLSearchParams, referrerHost: string | null, clickIdType: string | null): string {
  const utmMedium = (params.get("utm_medium") || "").toLowerCase();
  const utmSource = (params.get("utm_source") || "").toLowerCase();

  if (params.get("ref") || params.get("aff")) return "affiliate";
  if (clickIdType) return clickIdType;
  const paidMedium = /cpc|ppc|paid|ads?|display|cpm|retarget/.test(utmMedium);
  const organic = organicSource(utmSource, referrerHost, paidMedium);
  if (organic) return organic;
  // ChatGPT/Perplexity/Copilot append utm_source to cited links and often send no referrer.
  if (utmMedium) {
    if (paidMedium) return `paid_${utmSource || "other"}`;
    if (/email|newsletter/.test(utmMedium)) return "email";
    if (/social/.test(utmMedium)) return utmSource || "social";
    if (/affiliate|partner/.test(utmMedium)) return "affiliate";
    return utmSource ? `${utmMedium}_${utmSource}` : utmMedium;
  }
  if (utmSource) return utmSource;

  if (referrerHost) {
    for (const [needle, channel] of REFERRER_CHANNELS) {
      if (["organic_search", "ai_assistant"].includes(channel)) continue;
      if (referrerHost.includes(needle)) return channel;
    }
    return "referral";
  }
  return "direct";
}

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

/**
 * Record the current page view as an attribution touch.
 * Cheap, synchronous, localStorage-only — safe to call on every route change.
 */
export function captureTouch(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    let clickId: string | null = null;
    let clickIdType: string | null = null;
    for (const [param, type] of Object.entries(CLICK_IDS)) {
      const value = params.get(param);
      if (value) {
        clickId = value;
        clickIdType = type;
        break;
      }
    }

    const rawReferrer = document.referrer || "";
    let referrerHost: string | null = null;
    if (rawReferrer) {
      try {
        const host = new URL(rawReferrer).hostname.toLowerCase().replace(/^www\./, "");
        // Internal navigation is not a new acquisition touch.
        referrerHost = host === window.location.hostname.replace(/^www\./, "") ? null : host;
      } catch {
        referrerHost = null;
      }
    }

    const stored = read();
    const isNewTouch =
      !stored ||
      !!referrerHost ||
      !!clickId ||
      !!params.get("utm_source") ||
      !!params.get("utm_medium") ||
      !!params.get("ref");

    // Plain internal navigation on an existing visit: nothing new to learn.
    if (!isNewTouch) return;

    const touch: Touch = {
      channel: classify(params, referrerHost, clickIdType),
      source: params.get("utm_source"),
      medium: params.get("utm_medium"),
      campaign: params.get("utm_campaign"),
      term: params.get("utm_term"),
      content: params.get("utm_content"),
      referrer: rawReferrer ? rawReferrer.slice(0, 500) : null,
      referrer_host: referrerHost,
      landing_path: url.pathname + (url.search ? url.search.slice(0, 200) : ""),
      click_id: clickId ? clickId.slice(0, 255) : null,
      click_id_type: clickIdType,
      at: new Date().toISOString(),
    };

    const next: Stored = stored
      ? { first: stored.first, last: touch, count: (stored.count || 1) + 1 }
      : { first: touch, last: touch, count: 1 };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* attribution must never break a page render */
  }
}

/** Current stored attribution, if any (used by the account page / debugging). */
export function getAttribution(): Stored | null {
  return read();
}

/**
 * Persist attribution for a freshly created account. Runs once per user:
 * the row is a primary key on user_id, so a duplicate insert is a no-op.
 */
export async function flushAttribution(userId: string, createdAt?: string): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const flushedKey = `os_attribution_saved_${userId}`;
    if (localStorage.getItem(flushedKey)) return;

    // Only attach attribution to a fresh signup — an existing user logging in
    // on a new device would otherwise overwrite their real origin.
    if (createdAt) {
      const ageMs = Date.now() - new Date(createdAt).getTime();
      if (!Number.isFinite(ageMs) || ageMs > 60 * 60 * 1000) {
        localStorage.setItem(flushedKey, "skipped");
        return;
      }
    }

    const stored = read();
    if (!stored) return;

    // upsert + ignoreDuplicates so a returning user doesn't produce a noisy 409.
    const { error } = await supabase.from("user_attribution").upsert({
      user_id: userId,
      first_channel: stored.first.channel,
      first_source: stored.first.source,
      first_medium: stored.first.medium,
      first_campaign: stored.first.campaign,
      first_term: stored.first.term,
      first_content: stored.first.content,
      first_referrer: stored.first.referrer,
      first_referrer_host: stored.first.referrer_host,
      first_landing_path: stored.first.landing_path,
      first_click_id: stored.first.click_id,
      first_click_id_type: stored.first.click_id_type,
      first_seen_at: stored.first.at,
      last_channel: stored.last.channel,
      last_source: stored.last.source,
      last_medium: stored.last.medium,
      last_campaign: stored.last.campaign,
      last_referrer_host: stored.last.referrer_host,
      last_landing_path: stored.last.landing_path,
      last_seen_at: stored.last.at,
      touch_count: stored.count,
      signup_path: window.location.pathname,
      user_agent: navigator.userAgent.slice(0, 500),
    }, { onConflict: "user_id", ignoreDuplicates: true });

    // 23505 = row already exists, which is success for our purposes.
    if (!error || error.code === "23505") {
      localStorage.setItem(flushedKey, "1");
    }
  } catch {
    /* non-fatal */
  }
}
